import React, { useState } from 'react';
import { CaretDown, CaretUp, Checks, FolderOpen, GraduationCap, ClipboardText, House, Bus, PushPin } from '@phosphor-icons/react';
import './FeeBreakdownList.css';

function formatCurrency(amount) {
  return `GHS ${amount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function FeeBreakdownList({ fees, onCustomAmountClick, isCompact = true }) {
  const [expandedCategories, setExpandedCategories] = useState({});

  if (!fees || fees.length === 0) {
    return (
      <div className="fee-breakdown-empty">
        <FolderOpen size={48} weight="duotone" />
        <h4>No Fees Found</h4>
        <p>This term has no fees assigned</p>
      </div>
    );
  }

  // Group fees by type
  const groupedFees = fees.reduce((acc, fee) => {
    const type = fee.fee_type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(fee);
    return acc;
  }, {});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const feeTypeColors = {
    tuition: { bg: '#e0f2fe', border: '#0284c7', icon: GraduationCap },
    registration: { bg: '#f0e7fe', border: '#7c3aed', icon: ClipboardText },
    accommodation: { bg: '#fef3c7', border: '#d97706', icon: House },
    transport: { bg: '#dcfce7', border: '#059669', icon: Bus },
    other: { bg: '#f5f3ff', border: '#8b5cf6', icon: PushPin }
  };

  return (
    <div className="fee-breakdown-list">
      <div className="breakdown-wrapper">
        {Object.entries(groupedFees).map(([feeType, typeFees]) => {
          const isExpanded = expandedCategories[feeType] !== false;
          const color = feeTypeColors[feeType.toLowerCase()] || feeTypeColors.other;
          const totalDue = typeFees.reduce((sum, f) => sum + (f.amount_due || 0), 0);
          const totalPaid = typeFees.reduce((sum, f) => sum + (f.amount_paid || 0), 0);
          const totalDiscount = typeFees.reduce((sum, f) => sum + (f.discount || 0), 0);
          const totalBalance = totalDue - totalPaid - totalDiscount;

          return (
            <div key={feeType} className="fee-category-section">
              <button
                className="category-header"
                onClick={() => toggleCategory(feeType)}
                style={{ borderLeftColor: color.border }}
              >
                <div className="category-info">
                  <span className="category-icon" style={{ background: color.bg }}>
                    <color.icon size={20} weight="duotone" />
                  </span>
                  <div className="category-details">
                    <h4>{feeType}</h4>
                    <span className="category-count">{typeFees.length} item{typeFees.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                <div className="category-summary">
                  <div className="summary-item">
                    <span className="label">Due:</span>
                    <span className="value">{formatCurrency(totalDue)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Paid:</span>
                    <span className="value paid">{formatCurrency(totalPaid)}</span>
                  </div>
                  {totalBalance > 0 && (
                    <div className="summary-item">
                      <span className="label">Outstanding:</span>
                      <span className="value outstanding">{formatCurrency(totalBalance)}</span>
                    </div>
                  )}
                  <span className="toggle-icon">
                    {isExpanded ? <CaretUp size={20} /> : <CaretDown size={20} />}
                  </span>
                </div>
              </button>

              {isExpanded && (
                <div className="category-items">
                  {typeFees.map((fee, idx) => (
                    <div key={fee.id} className={`fee-item-card ${fee.status}`}>
                      <div className="fee-item-header">
                        <div className="fee-item-title">
                          <h5>{fee.fee_name || feeType}</h5>
                          <span className={`status-badge ${fee.status}`}>
                            {fee.status === 'paid' ? <><Checks size={12} weight="bold" /> Paid</> : 'Outstanding'}
                          </span>
                        </div>
                      </div>

                      <div className="fee-item-details">
                        <div className="detail-row">
                          <span className="detail-label">Amount Due:</span>
                          <span className="detail-value">{formatCurrency(fee.amount_due)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Amount Paid:</span>
                          <span className="detail-value paid">{formatCurrency(fee.amount_paid)}</span>
                        </div>
                        {fee.balance > 0 && (
                          <div className="detail-row outstanding">
                            <span className="detail-label">Outstanding:</span>
                            <span className="detail-value danger">{formatCurrency(fee.balance)}</span>
                          </div>
                        )}
                      </div>

                      {fee.balance > 0 && onCustomAmountClick && (
                        <button
                          className="btn-pay-individual"
                          onClick={() => onCustomAmountClick(fee)}
                          data-testid={`partial-pay-fee-${fee.id}`}
                        >
                          Pay Now
                        </button>
                      )}

                      {fee.balance === 0 && (
                        <div className="fee-paid-status">
                          <Checks size={18} weight="fill" /> Fully Paid
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </div>
  );
}

export default FeeBreakdownList;
